import React, { useState, useEffect } from "react";
import axios from "axios";
import { BsThreeDots } from "react-icons/bs";
import { MdOutlineEmail } from "react-icons/md";
import { BsCalendar3 } from "react-icons/bs";

const TicketWindow = (selectedChat) => {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("");
  const [showDropdown, setShowDropdown] = useState({});
  const orgName = sessionStorage.getItem('organizationName');

  useEffect(() => {
    if (selectedChat.id) {
      axios.get(`${process.env.REACT_APP_API_URL}/tickets/getTickets/${selectedChat.id}`,{
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
          },
        })
        .then((response) => {
          setTickets(
            response.data.map((ticket) => {
              let attachments = [];
              try {
                attachments = ticket.attachments ? JSON.parse(ticket.attachments) : [];
              } catch (_) {}
              return {
                id: ticket.ticket_id,
                status: ticket.status,
                subject: ticket.subject,
                description: ticket.ticket_message,
                created_at: ticket.created_at,
                attachments,
              };
            })
          );
        })
        .catch((err) => console.error("Error loading tickets:", err));
    }
  }, [selectedChat.id]);

  const handleChange = async (e, ticketId) => {
    e.preventDefault();
    const newStatus = e.target.value;

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/tickets/updateTicketStatus`, {
        ticket_id: ticketId,
        status: newStatus,
      });

      setStatus(newStatus);
      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
        )
      );

      // Hide dropdown after selecting a status
      setShowDropdown((prev) => ({ ...prev, [ticketId]: false }));
    } catch (error) {
      console.error("Error updating ticket status:", error);
    }
  };

  const toggleDropdown = (ticketId) => {
    setShowDropdown((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden">
      <div className="p-3 bg-gray-100 border-b">
        <h1 className="text-base text-gray-800 font-semibold">
          {selectedChat.name.charAt(0).toUpperCase() +
            selectedChat.name.slice(1, 10).split("@")[0]}
        </h1>
      </div>

      <div className="flex-1 p-4 space-y-3 overflow-y-auto bg-gray-50">
        {tickets.map((ticket) => {
          let statusBadgeClasses = "";
          if (ticket.status === "open") statusBadgeClasses = "bg-green-100 text-green-700";
          else if (ticket.status === "inprogress") statusBadgeClasses = "bg-yellow-100 text-yellow-700";
          else if (ticket.status === "closed") statusBadgeClasses = "bg-blue-100 text-blue-700";

          const statusLabel =
            ticket.status === "open" ? "Open" :
            ticket.status === "inprogress" ? "In Progress" :
            "Closed";

          const avatarLetter = (selectedChat.name || "T").charAt(0).toUpperCase();

          return (
            <div
              key={ticket.id}
              className="relative flex items-start gap-3 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 w-full"
            >
              {/* Avatar */}
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold mt-0.5">
                {avatarLetter}
              </div>

              {/* Body */}
              <div className="flex-1 min-w-0 pr-10">
                {/* Ticket # + status badge */}
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium text-gray-500">Ticket #{ticket.id}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadgeClasses}`}>
                    {statusLabel}
                  </span>
                </div>

                {/* Subject */}
                <p className="text-base font-semibold text-gray-800 break-words leading-snug">
                  {ticket.subject}
                </p>

                {/* Description */}
                <p className="text-sm text-black-500 break-words mt-0.5 leading-relaxed">
                  {ticket.description}
                </p>

                {/* Attachments */}
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ticket.attachments.map((url, idx) => {
                      const isImage = /\.(jpe?g|png|gif|webp)$/i.test(url);
                      const fileName = url.split('/').pop();
                      const fullUrl = `${process.env.REACT_APP_API_URL}${url}`;
                      return isImage ? (
                        <a key={idx} href={fullUrl} target="_blank" rel="noreferrer" title={fileName}>
                          <img
                            src={fullUrl}
                            alt={fileName}
                            className="w-16 h-16 object-cover rounded-md border border-gray-200 hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ) : (
                        <a
                          key={idx}
                          href={fullUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-100 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 10-5.656-5.656L5.757 10.757a6 6 0 108.485 8.485L19 14" />
                          </svg>
                          <span className="truncate max-w-[140px]">{fileName}</span>
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Email + date row */}
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <MdOutlineEmail size={13} />
                    {selectedChat.name}
                  </span>
                  <span className="flex items-center gap-1">
                    <BsCalendar3 size={11} />
                    {new Date(ticket.created_at).toLocaleString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>

              {/* Status dropdown — always visible, top-right */}
              <div className="absolute top-3 right-10">
                <select
                  value={ticket.status}
                  onChange={(e) => handleChange(e, ticket.id)}
                  className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer"
                >
                  <option value="open">Open</option>
                  <option value="inprogress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Three-dots menu */}
              {/* <div className="absolute top-3 right-3 cursor-pointer text-gray-400 hover:text-gray-600">
                <BsThreeDots size={18} onClick={() => toggleDropdown(ticket.id)} />
              </div> */}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TicketWindow;
